import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const tooltipStyle = {
  background: "rgba(255,255,255,0.95)",
  border: "1px solid rgba(0,0,0,0.06)",
  borderRadius: 12,
  fontSize: 12,
  fontFamily: "IBM Plex Sans",
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
};
const tooltipStyleDark = {
  background: "rgba(20,20,26,0.95)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  fontSize: 12,
  fontFamily: "IBM Plex Sans",
  color: "#fff",
  boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
};
function useTooltipStyle() {
  if (typeof window === "undefined") return tooltipStyle;
  return document.documentElement.classList.contains("dark") ? tooltipStyleDark : tooltipStyle;
}

// ── Status distribution donut ────────────────────────────────────
export function StatusDonut({ stats = {}, title = "Status distribution", subtitle = "Where things stand" }) {
  const palette = ["#0047FF", "#22D3EE", "#BEF264", "#F59E0B", "#EF4444", "#8B5CF6"];
  const data = Object.entries(stats)
    .map(([k, v], i) => ({ name: k.replace(/_/g, " "), value: v, color: palette[i % palette.length] }))
    .filter((x) => x.value > 0);
  const total = data.reduce((s, x) => s + x.value, 0);

  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-status-donut">
      <div className="overline">{title}</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">{subtitle}</h3>
      <div className="grid grid-cols-[160px_1fr] gap-4 mt-2 items-center">
        <div className="h-40 relative">
          {data.length === 0 ? (
            <div className="h-full grid place-items-center text-muted-foreground text-xs">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" innerRadius={44} outerRadius={70} paddingAngle={2} stroke="none">
                  {data.map((c) => <Cell key={c.name} fill={c.color} />)}
                </Pie>
                <Tooltip contentStyle={useTooltipStyle()} />
              </PieChart>
            </ResponsiveContainer>
          )}
          {data.length > 0 && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="metric text-2xl">{total}</div>
                <div className="overline">total</div>
              </div>
            </div>
          )}
        </div>
        <ul className="text-sm space-y-1.5">
          {data.length === 0 && <li className="text-muted-foreground text-xs">Nothing to chart yet.</li>}
          {data.map((c) => (
            <li key={c.name} className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
              <span className="flex-1 truncate capitalize">{c.name}</span>
              <span className="text-mono text-xs text-muted-foreground">{total ? Math.round((c.value / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Generic area line chart card ─────────────────────────────────
export function TrendChart({ data = [], dataKey = "value", title = "Trend", subtitle = "Over time", color = "#0047FF" }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-trend">
      <div className="overline">{title}</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">{subtitle}</h3>
      <div className="h-56 mt-3">
        {data.length === 0 ? (
          <div className="h-full grid place-items-center text-muted-foreground text-xs">No data in this range</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: -10, right: 10, top: 10 }}>
              <defs>
                <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor={color} stopOpacity={0.30} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={useTooltipStyle()} />
              <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} fill={`url(#grad-${dataKey})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ── Bar chart ────────────────────────────────────────────────────
export function BarChartCard({ data = [], dataKey = "value", title = "Categories", subtitle = "Volume by bucket", color = "#0047FF" }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-bar">
      <div className="overline">{title}</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">{subtitle}</h3>
      <div className="h-56 mt-3">
        {data.length === 0 ? (
          <div className="h-full grid place-items-center text-muted-foreground text-xs">No data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ left: -10, right: 10, top: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={useTooltipStyle()} cursor={{ fill: "rgba(0,71,255,0.06)" }} />
              <Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ── Multi-line chart ─────────────────────────────────────────────
export function PlatformGrowth({ data = [], title = "Last 6 months", subtitle = "Platform growth" }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-platform-growth">
      <div className="overline">{title}</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">{subtitle}</h3>
      <div className="h-56 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ left: -10, right: 10, top: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={useTooltipStyle()} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="bookings"   name="Bookings"   stroke="#0047FF" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="registrations" name="Registrations" stroke="#22D3EE" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="checkins"    name="Check-ins"  stroke="#BEF264" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
